'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLayout from '@/components/AppLayout';
import ProjectProgressHero from './components/ProjectProgressHero';
import ClientStatusCards from './components/ClientStatusCards';
import DeliverablesPanel from './components/DeliverablesPanel';
import ClientInvoiceSummary from './components/ClientInvoiceSummary';
import MessagesTeaser from './components/MessagesTeaser';
import DocumentsPanel from './components/DocumentsPanel';
import { getClientProject } from '@/lib/services/portalService';

export default function ClientDashboardPage() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [project, setProject] = useState<any>(null);
  const [projectLoading, setProjectLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      router?.replace('/login');
    }
  }, [user, loading, router]);

  useEffect(() => {
    if (user) {
      const role = user?.user_metadata?.role || user?.app_metadata?.role;
      if (role === 'admin') {
        router?.replace('/admin-dashboard');
        return;
      }
      getClientProject(user?.id)?.then((p) => {
        setProject(p);
        setProjectLoading(false);
      });
    }
  }, [user, router]);

  if (loading || projectLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-muted-foreground">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <AppLayout role="client" user={user}>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-12 py-8 space-y-8">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {project?.project_name || 'My Renovation Project'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {project?.description || 'Your project details'} · Started{' '}
              {project?.start_date
                ? new Date(project.start_date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
            <svg className="w-3.5 h-3.5 text-positive" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            Last updated {new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>

        <ProjectProgressHero project={project} />
        <ClientStatusCards project={project} />

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <DeliverablesPanel projectId={project?.id} />
            <ClientInvoiceSummary clientId={user?.id} />
          </div>
          <div className="space-y-6">
            <MessagesTeaser projectId={project?.id} userId={user?.id} userName={user?.user_metadata?.full_name || 'Client'} />
            <DocumentsPanel projectId={project?.id} userId={user?.id} uploaderType="client" />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}